import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  setProgressBarStages,
  updateProgressBarStage,
} from '../../Lib/redux/slices/navigation';
import {
  buildStages,
  StageStatus,
} from '../../Components/Progress/Progress.helpers';
import { findPostcodePageNumbers } from '../../Components/__helpers__/component.helpers';

/**
 * Custom hook to manage the state of the staged progress bar
 * @param {Object} options - Options for the hook
 * @param {boolean} options.enableStagedProgressBar - Whether the staged progress bar is enabled
 * @param {number} options.currentPage - The current page number
 * @param {number} options.submissionPageNumber - The submission page number
 * @param {Array} options.components - The components array
 * @returns {void}
 */
const useStagedProgressBar = ({
  enableStagedProgressBar,
  currentPage,
  submissionPageNumber,
  components,
}) => {
  const dispatch = useDispatch();
  const { pageNumbers } = useSelector((store) => store.paging);
  const { progressBarStages } = useSelector((store) => store.navigation);

  useEffect(() => {
    if (enableStagedProgressBar && progressBarStages.length === 0) {
      const firstPostcodePageNumber = findPostcodePageNumbers(components)[0];

      const totalPages = pageNumbers.length;
      const finishPageNumber =
        typeof submissionPageNumber === 'number'
          ? submissionPageNumber
          : totalPages - 1;

      const updatedStages = buildStages(
        firstPostcodePageNumber,
        finishPageNumber,
        pageNumbers
      );
      dispatch(setProgressBarStages({ progressBarStages: updatedStages }));
    }
  }, [
    currentPage,
    submissionPageNumber,
    pageNumbers,
    components,
    dispatch,
    enableStagedProgressBar,
    progressBarStages,
  ]);

  // Use a separate effect to update stage statuses based on current page
  // This approach prevents infinite loops by using a different pattern
  useEffect(() => {
    if (!enableStagedProgressBar || progressBarStages?.length === 0) {
      return;
    }

    const completeAnySkippedStages = (stage) => {
      const previousStage =
        progressBarStages[
          progressBarStages.findIndex((s) => s.id === stage.id) - 1
        ];
      if (
        previousStage &&
        previousStage.pages.length === 0 &&
        previousStage.status !== StageStatus.COMPLETED
      ) {
        stagesToUpdate.push({
          ...previousStage,
          status: StageStatus.COMPLETED,
        });
      }
    };

    // Instead of updating each stage individually, create a batch of changes
    const stagesToUpdate = [];

    progressBarStages.forEach((stage) => {
      let newStatus = stage.status;
      if (
        stage.pages.includes(currentPage) &&
        stage.status !== StageStatus.COMPLETED
      ) {
        newStatus = StageStatus.CURRENT;
        completeAnySkippedStages(stage);
      } else if (stage.pages.some((page) => page < currentPage)) {
        newStatus = StageStatus.COMPLETED;
      }

      // Only add to update batch if status changed
      if (newStatus !== stage.status) {
        stagesToUpdate.push({ ...stage, status: newStatus });
      }
    });

    // Only dispatch if we have changes to make
    if (stagesToUpdate.length > 0) {
      // Update each changed stage
      stagesToUpdate.forEach((stage) => {
        dispatch(updateProgressBarStage({ progressBarStage: stage }));
      });
    }
  }, [currentPage, progressBarStages, dispatch, enableStagedProgressBar]);

  return { stages: progressBarStages };
};

export default useStagedProgressBar;
